import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatDialogHelper } from 'src/app/helpers/material/mat-dialog-helper';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import { IP_BOARD_PART_TYPE_CHOICES } from 'src/app/model/product/ip-board/ip-board-part-type';
import { AdhesivePlacementTypeChoice } from 'src/app/model/product/ip-board/ip-board-placement-type';
import { Project } from 'src/app/model/project/project';
import { SignTypesChoices } from 'src/app/model/sign/enums/sign-type';
import {
  ModalService,
  ObservableHandlerPair,
} from 'src/app/services/modal.service';
import {
  ProjectDisplayObservableService,
  SignSelectedEvent,
} from 'src/app/services/project-display-observable.service';
import { SnackService } from 'src/app/services/snack.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { ClientNoStockComponent } from '../client-sign-selector/client-no-stock/client-no-stock.component';
import { ClientOrderStockComponent } from '../client-sign-selector/client-order-stock/client-order-stock.component';
import { StockOrderedEvent } from '../client-sign-selector/client-order-stock/helper/services/client-order-stock-controller-request.service';
import { StockOrderedEventService } from '../client-sign-selector/client-order-stock/helper/services/stock-orderered-event.service';
import { ClientSignSelectorHelper } from '../client-sign-selector/client-sign-selector-helper';
import { SignWithStockAmount } from '../client-sign-selector/sign-with-stock-amount';
import {
  ClientIPBoardAndPlacementHelper,
  SignBoardOrPlacementOption,
} from '../helper/client-ip-board-and-placement-helper';
import {
  ClientIPBoardPartFormHelper,
  CustomSignOption,
} from '../helper/client-ip-board-part-form-helper';

@Component({
  selector: 'client-ip-board-part-form',
  templateUrl: './client-ip-board-part-form.component.html',
  styleUrls: ['./client-ip-board-part-form.component.scss'],
})
export class ClientIPBoardPartFormComponent
  extends SubscriptionBasedComponent
  implements OnInit, AfterViewInit
{
  @Input()
  ipBoard: IPBoard;
  @Input()
  ipBoardPart: IPBoardPart;
  @Input()
  project: Project;
  @Input()
  verifySelections: boolean = true;
  @Input()
  isLeftRight = false;

  @Output()
  save = new EventEmitter<any>();

  @ViewChildren('boardTypeSelector', { read: ElementRef })
  boardTypeSelector: QueryList<ElementRef>;
  @ViewChildren('placementTypeSelector', { read: ElementRef })
  placementTypeSelector: QueryList<ElementRef>;

  SIGN_TYPE_CHOICES = SignTypesChoices;
  IP_BOARD_PART_TYPE_CHOICES = IP_BOARD_PART_TYPE_CHOICES;

  CUSTOM_SIGN_ADHESIVE_OPTIONS_ARRAY =
    ClientIPBoardPartFormHelper.CUSTOM_SIGN_ADHESIVE_OPTIONS_ARRAY;

  customSelectedRightOption: CustomSignOption;

  IP_BOARD_BOARD_OPTIONS =
    ClientIPBoardAndPlacementHelper.IP_BOARD_BOARD_OPTIONS;
  IP_BOARD_BOARD_OPTIONS_ARRAY =
    ClientIPBoardAndPlacementHelper.IP_BOARD_BOARD_OPTIONS_ARRAY;
  IP_BOARD_LEFT_RIGHT_BOARD_OPTIONS_ARRAY =
    ClientIPBoardAndPlacementHelper.IP_BOARD_LEFT_RIGHT_BOARD_OPTIONS_ARRAY;

  SINGLE_SIDED_PLACEMENT_OPTIONS_ARRAY =
    ClientIPBoardAndPlacementHelper.SINGLE_SIDED_PLACEMENT_OPTIONS_ARRAY;
  DOUBLE_SIDED_PLACEMENT_OPTIONS_ARRAY =
    ClientIPBoardAndPlacementHelper.DOUBLE_SIDED_PLACEMENT_OPTIONS_ARRAY;
  V_IP_BOARD_PLACEMENT_OPTIONS_ARRAY =
    ClientIPBoardAndPlacementHelper.V_IP_BOARD_PLACEMENT_OPTIONS_ARRAY;
  V_ADHESIVE_PLACEMENT_OPTIONS_ARRAY =
    ClientIPBoardAndPlacementHelper.V_ADHESIVE_PLACEMENT_OPTIONS_ARRAY;

  selectedBoardTypeOption: SignBoardOrPlacementOption;
  selectedPlacementTypeOption: SignBoardOrPlacementOption;
  selectedAdhesiveBoardPlacementOption: AdhesivePlacementTypeChoice;

  selectedSignOptionId: number;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private snackService: SnackService,
    private modalService: ModalService,
    private stockOrderedEventService: StockOrderedEventService
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.ipBoardPart.id != null) {
      this.initializeChoices();
    }

    this.addSubscription(
      this.projectDisplayObservableService.getSignSelectedSubscribable(),
      (event: SignSelectedEvent) => {
        if (event.ipBoardPart === this.ipBoardPart) {
          this.selectedSignOptionId = event.selectedOptionId;
          if (event.toStandardOrToCustom || event.resetLeftRight) {
            this.resetSelections(event.adhesiveStockSign);
          }
          if (event.setAdhesive) {
            this.setAdhesive();
          }
        }
      }
    );
    this.addSubscription(
      this.projectDisplayObservableService.getResetLeftRightSubscribable(),
      (event: boolean) => {
        if (event) {
          this.resetSelections(false);
        }
      }
    );
  }

  ngAfterViewInit(): void {
    if (this.ipBoardPart.id == null) {
      this.addSubscription(
        this.boardTypeSelector.changes,
        (list: QueryList<ElementRef>) => this.scrollToSingletonQueryList(list)
      );
      this.addSubscription(
        this.placementTypeSelector.changes,
        (list: QueryList<ElementRef>) => this.scrollToSingletonQueryList(list)
      );
    }
  }

  private scrollToSingletonQueryList(list: QueryList<ElementRef>): void {
    if (
      list != null &&
      list.first != null &&
      list.first.nativeElement != null
    ) {
      list.first.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  private initializeChoices(): void {
    if (this.ipBoardPart.sign == null) {
      this.customSelectedRightOption =
        ClientIPBoardPartFormHelper.getInitialSelectedRightOption(
          this.ipBoardPart
        );
    }
    this.selectedBoardTypeOption =
      ClientIPBoardAndPlacementHelper.getInitialSelectedBoardOption(
        this.ipBoardPart
      );
    if (ClientIPBoardAndPlacementHelper.isAdhesiveBoard(this.ipBoardPart)) {
      this.selectedPlacementTypeOption =
        ClientIPBoardAndPlacementHelper.getInitialSelectedAdhesiveBoardPlacementOption(
          this.ipBoardPart
        );
    } else {
      this.selectedPlacementTypeOption =
        ClientIPBoardAndPlacementHelper.getInitialSelectedIPBoardPlacementOption(
          this.ipBoardPart
        );
    }
    this.selectedSignOptionId =
      ClientSignSelectorHelper.getSelectedSignOptionId(
        this.ipBoardPart,
        this.project.collaborative
      );
  }

  setCustomSelectedRightOption(
    customSelectedRightOption: CustomSignOption
  ): void {
    customSelectedRightOption.onSelect(this.ipBoardPart);
    this.customSelectedRightOption = customSelectedRightOption;
    this.projectDisplayObservableService
      .getIPBoardConfigurationChangedSubscribable()
      .next({ ipBoardPart: this.ipBoardPart });
    this.save.emit();
  }

  selectBoardOption(
    signBoardOption: SignBoardOrPlacementOption,
    ignoreStock: boolean = false
  ): void {
    if (
      !ignoreStock &&
      this.ipBoardPart != null &&
      this.ipBoardPart.sign != null &&
      !this.ipBoardPart.sign.withoutStock &&
      this.getStockAmountAvailableForNewOrder() < 2 &&
      [BoardTypeChoices.DOUBLE_SIDED.label, BoardTypeChoices.V.label].includes(
        signBoardOption.label
      )
    ) {
      this.openNoStockDialog(signBoardOption);
    }
    try {
      this.doSelectBoardOrPlacement(signBoardOption);
      this.selectedBoardTypeOption = signBoardOption;
      this.selectedPlacementTypeOption = null;
      this.projectDisplayObservableService
        .getIPBoardConfigurationChangedSubscribable()
        .next({ ipBoardPart: this.ipBoardPart });
      this.save.emit();
    } catch (err) {
      this.snackService.showError(err);
    }
  }

  private getStockAmountAvailableForNewOrder(): number {
    const ordered =
      this.ipBoardPart.sign.orderedAmount != null
        ? this.ipBoardPart.sign.orderedAmount
        : this.ipBoardPart.sign.inOfferStock != null
        ? this.ipBoardPart.sign.inOfferStock
        : 0;
    const currentStock =
      (this.ipBoardPart.sign.totalStock == null
        ? 0
        : this.ipBoardPart.sign.totalStock) +
      ordered +
      (this.ipBoardPart.sign.inProductionStock == null
        ? 0
        : this.ipBoardPart.sign.inProductionStock);
    return currentStock;
  }

  selectPlacementOption(signPlacementOption: SignBoardOrPlacementOption): void {
    try {
      this.doSelectBoardOrPlacement(signPlacementOption);
      this.selectedPlacementTypeOption = signPlacementOption;
      this.projectDisplayObservableService
        .getIPBoardConfigurationChangedSubscribable()
        .next({ ipBoardPart: this.ipBoardPart });
      this.save.emit();
    } catch (err) {
      this.snackService.showError(err);
    }
  }

  private doSelectBoardOrPlacement(
    signBoardOrPlacementOption: SignBoardOrPlacementOption
  ) {
    if (signBoardOrPlacementOption == null) {
      return;
    }
    if (this.verifySelections) {
      signBoardOrPlacementOption.verify(this.ipBoard, this.ipBoardPart);
    }
    signBoardOrPlacementOption.onSelect(this.ipBoardPart);
  }

  private setAdhesive() {
    this.ipBoardPart.boardType = BoardTypeChoices.V;
    this.selectedBoardTypeOption =
      ClientIPBoardAndPlacementHelper.ADHESIVE_BOARD_BOARD_OPTIONS.V;

    if (this.customSelectedRightOption != null) {
      this.customSelectedRightOption.onSelect(this.ipBoardPart);
      this.save.emit();
    }
  }

  private resetSelections(isAdhesiveStockSign: boolean): void {
    if (isAdhesiveStockSign) {
      this.setAdhesive();
    } else {
      this.ipBoardPart.boardType = null;
      this.selectedBoardTypeOption = null;
    }
    this.ipBoardPart.placementType = null;
    this.ipBoardPart.alternativePlacementType = null;
    this.selectedPlacementTypeOption = null;
    this.selectedAdhesiveBoardPlacementOption = null;
    this.customSelectedRightOption = null;
    this.projectDisplayObservableService
      .getIPBoardConfigurationChangedSubscribable()
      .next({ ipBoardPart: this.ipBoardPart });
  }

  private openNoStockDialog(signBoardOption: SignBoardOrPlacementOption): void {
    this.modalService.openComponentInModal(
      ClientNoStockComponent,
      [{ key: 'isDoubleSided', value: this.project }],
      new ObservableHandlerPair(
        (modalRef) =>
          MatDialogHelper.getClosedObservable(modalRef, 'intendOrderStock'),
        (res: { intendOrderStock: boolean; autoSelectNoStock: boolean }) => {
          if (res == null) {
            this.selectBoardOption(null, true);
          } else if (res.intendOrderStock) {
            this.openOrderStockDialog(this.ipBoardPart.sign);
          } else if (res.autoSelectNoStock) {
            this.selectBoardOption(signBoardOption, true);
          } else {
            this.selectBoardOption(null, true);
          }
        }
      ),
      { maxWidth: 550, panelClass: 'border-modal-panel-class' }
    );
  }

  openOrderStockDialog(value: SignWithStockAmount): void {
    if (
      this.project != null &&
      this.project.projectContacts != null &&
      this.project.projectContacts.length > 0 &&
      this.project.projectContacts[0].company != null
    ) {
      this.modalService.openComponentInModal(
        ClientOrderStockComponent,
        [
          { key: 'sign', value: value },
          {
            key: 'companyId',
            value: this.project.projectContacts[0].company.id,
          },
        ],
        new ObservableHandlerPair(
          (modalRef) =>
            MatDialogHelper.getClosedObservable(modalRef, 'stockOrdered'),
          (event: StockOrderedEvent) => {
            if (event != null) {
              this.stockOrderedEventService
                .getStockOrderedEventSubscribable()
                .next(event);
            } else {
              this.selectBoardOption(null, true);
            }
          }
        ),
        { minWidth: 850, panelClass: 'border-modal-panel-class' }
      );
    }
  }
}
